import { API_BASE_URL } from '../../../config';
import type { Trip, TripDetail, CreateTripData, UpdateTripData, TripsResponse } from '../types';

const API_BASE = `${API_BASE_URL}/api/v1/users/me/trips`;

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/** Fetch all trips for the authenticated user */
export async function fetchTrips(): Promise<Trip[]> {
  const response = await fetch(API_BASE, {
    headers: getAuthHeaders(),
  });
  const data = await handleResponse<any>(response);
  return data.trips || data.data || [];
}

/** Fetch a single trip by ID with pins and photos */
export async function fetchTrip(tripId: string): Promise<TripDetail> {
  const response = await fetch(`${API_BASE}/${tripId}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<TripDetail>(response);
}

/** Create a new trip */
export async function createTrip(data: CreateTripData): Promise<Trip> {
  const response = await fetch(API_BASE, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<Trip>(response);
}

/** Update an existing trip */
export async function updateTrip(tripId: string, data: UpdateTripData): Promise<Trip> {
  const response = await fetch(`${API_BASE}/${tripId}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<Trip>(response);
}

/** Delete a trip */
export async function deleteTrip(tripId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/${tripId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Delete failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
}
